// Designer-side fetch engine for `api`-sourced page variables.
//
// Mirrors the runtime PageVarsService.fetchApiVariable behaviour from
// vision-ang-runtime/smart-page so the designer preview can populate
// variables (including `type: 'json'` ones that drive bindings) without
// having to flip over to the Angular runtime iframe.
//
// Scope: best-effort. The full runtime engine also re-evaluates
// computed expressions and cascades multi-step triggers; this hook
// keeps the behaviour minimal:
//   1. Fire `api.autoload === true` requests on first render.
//   2. Re-fire any variable whose `api.triggerOn` includes a watched
//      variable when that watched variable changes.
//   3. Substitute `{{<input_name>}}` and `{{<input_name>.path>}}` tokens
//      in url / headers / body strings before sending.
//   4. Write the response (whole body if `pickPath` omitted, otherwise
//      the picked subtree) back into the values map via the caller.

import { useEffect, useRef } from 'react'

import type { PageInput, ApiVariableSource } from './types'
import { getPath, toDisplayString } from './actions/substitute'

type ValueMap = Record<string, unknown>

// Resolve {{tokens}} against the current values map. Mirrors the
// page-vars resolver in the Angular runtime — strings get scalar
// substitution; dotted paths walk into object values.
function resolveTokens(text: string, values: ValueMap): string {
  return text.replace(/\{\{\s*([\w.[\]]+)\s*\}\}/g, (_match, expr: string) => {
    if (Object.prototype.hasOwnProperty.call(values, expr)) {
      return toDisplayString(values[expr])
    }
    const dot = expr.indexOf('.')
    if (dot > 0) {
      const head = expr.slice(0, dot)
      const tail = expr.slice(dot + 1)
      if (Object.prototype.hasOwnProperty.call(values, head)) {
        return toDisplayString(getPath(values[head], tail))
      }
    }
    return ''
  })
}

function resolveDeep(value: unknown, values: ValueMap): unknown {
  if (typeof value === 'string') return resolveTokens(value, values)
  if (Array.isArray(value)) return value.map((v) => resolveDeep(v, values))
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = resolveDeep(v, values)
    }
    return out
  }
  return value
}

// `triggerOn` is a string[] per the schema, but hand-edited / AI-authored
// models sometimes carry a bare string (a single trigger). Coerce to an
// array so the read sites below can safely call array methods — a scalar
// would otherwise throw (`.join`/`.some` are not string methods) during
// render and white-screen the whole page.
function triggerKeys(inp: PageInput): string[] {
  const t: unknown = inp.api?.triggerOn
  if (Array.isArray(t)) return t.filter((x): x is string => typeof x === 'string')
  if (typeof t === 'string' && t.trim() !== '') return [t]
  return []
}

async function fetchApiVariable(
  inp: PageInput,
  values: ValueMap,
  signal: AbortSignal,
  integrationInvokeUrl?: (apiName: string) => string,
): Promise<unknown> {
  if (!inp.api) return undefined
  const src: ApiVariableSource = inp.api

  // IOFlow-routed source: POST through the server-side invoke proxy (the
  // browser never holds the invocation key); url/headers are ignored.
  const ioflowApi = (src.ioflow_api || '').trim()
  let url: string
  let init: RequestInit
  if (ioflowApi) {
    if (!integrationInvokeUrl) {
      throw new Error(`IOFlow API "${ioflowApi}" is not available on this surface`)
    }
    url = integrationInvokeUrl(ioflowApi)
    const body = src.body === undefined ? {} : resolveDeep(src.body, values)
    init = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal,
    }
  } else {
    url = resolveTokens(src.url, values)
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    for (const [k, v] of Object.entries(src.headers ?? {})) {
      headers[k] = resolveTokens(String(v), values)
    }
    init = { method: src.method, headers, signal }
    if (src.method !== 'GET' && src.body !== undefined) {
      const body = typeof src.body === 'string'
        ? resolveTokens(src.body, values)
        : JSON.stringify(resolveDeep(src.body, values))
      init.body = body
    }
  }
  const res = await fetch(url, init)
  if (!res.ok) {
    throw new Error(`${res.status} ${res.statusText}`.trim() || 'Request failed')
  }
  // Try JSON first; fall back to text if the server didn't send JSON.
  const text = await res.text()
  let parsed: unknown
  try { parsed = text === '' ? null : JSON.parse(text) } catch { parsed = text }
  // The invoke proxy wraps the flow's body as {success, response} — unwrap.
  if (ioflowApi && parsed && typeof parsed === 'object'
      && (parsed as Record<string, unknown>).success === true
      && 'response' in (parsed as Record<string, unknown>)) {
    parsed = (parsed as Record<string, unknown>).response
  }
  return src.pickPath ? getPath(parsed, src.pickPath) : parsed
}

export interface PageApiVariablesHooks {
  onLoading?: (name: string, loading: boolean) => void
  onError?: (name: string, message: string | null) => void
}

/**
 * Hook: fires `api.autoload` requests and `api.triggerOn` cascades for
 * the given inputs. Calls `setValue(name, value)` when a fetch resolves.
 * Loading + error events are surfaced through optional callbacks for
 * the property panel to render a status pill.
 *
 * Re-runs whenever the *identity* of `inputs` changes (the page
 * designer mutates `page.inputs` immutably on edit) or whenever any
 * watched value changes. We don't re-run on every keystroke into a
 * value that nobody listens for — only on changes to keys actually in
 * a `triggerOn` set, computed up front.
 */
export function usePageApiVariables(
  inputs: PageInput[],
  values: ValueMap,
  setValue: (name: string, value: unknown) => void,
  hooks?: PageApiVariablesHooks,
  /** Surface-relative IOFlow invoke proxy URL (see ActionCtx.integrationInvokeUrl). */
  integrationInvokeUrl?: (apiName: string) => string,
): void {
  // Cache the latest values in a ref so the effect can read fresh
  // values without restarting on every keystroke.
  const valuesRef = useRef(values)
  valuesRef.current = values
  const setValueRef = useRef(setValue)
  setValueRef.current = setValue
  const hooksRef = useRef(hooks)
  hooksRef.current = hooks
  const integrationInvokeUrlRef = useRef(integrationInvokeUrl)
  integrationInvokeUrlRef.current = integrationInvokeUrl

  // Track in-flight requests per variable so a fast-changing trigger
  // doesn't cause two concurrent fetches to race for "last writer".
  const controllersRef = useRef<Map<string, AbortController>>(new Map())

  // Build the set of watched keys — any input that appears in another
  // input's `triggerOn`. Keeps the dependency-change loop cheap.
  const watchedKeysSig = inputs
    .filter((i) => triggerKeys(i).length)
    .map((i) => `${i.name}:${triggerKeys(i).join(',')}`)
    .join('|')

  // Auto-load: fire on first run + whenever the inputs array identity
  // changes (e.g. the author adds/removes a variable).
  useEffect(() => {
    let cancelled = false
    const fireFor = (inp: PageInput) => {
      if (!inp.api) return
      const prev = controllersRef.current.get(inp.name)
      try { prev?.abort() } catch { /* ignore */ }
      const ctrl = new AbortController()
      controllersRef.current.set(inp.name, ctrl)
      hooksRef.current?.onLoading?.(inp.name, true)
      hooksRef.current?.onError?.(inp.name, null)
      fetchApiVariable(inp, valuesRef.current, ctrl.signal, integrationInvokeUrlRef.current)
        .then((value) => {
          if (cancelled) return
          setValueRef.current(inp.name, value)
        })
        .catch((err) => {
          if (cancelled || ctrl.signal.aborted) return
          const msg = err instanceof Error ? err.message : 'Request failed'
          hooksRef.current?.onError?.(inp.name, msg)
        })
        .finally(() => {
          if (!cancelled) hooksRef.current?.onLoading?.(inp.name, false)
        })
    }

    for (const inp of inputs) {
      if (inp.api?.autoload) fireFor(inp)
    }

    return () => {
      cancelled = true
      for (const ctrl of controllersRef.current.values()) {
        try { ctrl.abort() } catch { /* ignore */ }
      }
      controllersRef.current.clear()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputs])

  // Trigger cascades: when any watched key's value changes, re-fire
  // every input whose `triggerOn` includes that key.
  const watchedNames = inputs
    .flatMap((i) => triggerKeys(i))
    .filter((n, i, a) => a.indexOf(n) === i)
  const watchedSig = watchedNames.map((n) => `${n}=${toDisplayString(values[n])}`).join('|')

  useEffect(() => {
    let cancelled = false
    for (const inp of inputs) {
      const triggers = triggerKeys(inp)
      if (triggers.length === 0) continue
      // Only fire when at least one trigger has a non-empty value —
      // avoids issuing an /api/.../{{empty}} request on initial mount
      // before the upstream input is set.
      const anyResolved = triggers.some((t) => {
        const v = valuesRef.current[t]
        return v !== undefined && v !== null && v !== ''
      })
      if (!anyResolved) continue
      const prev = controllersRef.current.get(inp.name)
      try { prev?.abort() } catch { /* ignore */ }
      const ctrl = new AbortController()
      controllersRef.current.set(inp.name, ctrl)
      hooksRef.current?.onLoading?.(inp.name, true)
      hooksRef.current?.onError?.(inp.name, null)
      fetchApiVariable(inp, valuesRef.current, ctrl.signal, integrationInvokeUrlRef.current)
        .then((value) => {
          if (cancelled) return
          setValueRef.current(inp.name, value)
        })
        .catch((err) => {
          if (cancelled || ctrl.signal.aborted) return
          const msg = err instanceof Error ? err.message : 'Request failed'
          hooksRef.current?.onError?.(inp.name, msg)
        })
        .finally(() => {
          if (!cancelled) hooksRef.current?.onLoading?.(inp.name, false)
        })
    }
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedSig, watchedKeysSig])
}
